"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";

import { cn } from "@postcheesy/core-utils";
import { cva, type VariantProps } from "class-variance-authority";

const Accordion = AccordionPrimitive.Root;

const accordionItemVariants = cva("", {
  variants: {
    variant: {
      default: "-borderb",
      muted: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface AccordionItemProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>,
    VariantProps<typeof accordionItemVariants> {}

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  AccordionItemProps
>(({ className, variant, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(accordionItemVariants({ variant, className }))}
    {...props}
  />
));
AccordionItem.displayName = "AccordionItem";

const accordionTriggerVariants = cva(
  "flex flex-1 items-center font-medium  transition-all [&[data-state=open]>svg]:rotate-180",
  {
    variants: {
      variant: {
        default: "justify-between py-4",
        muted: "justify-start gap-4",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>,
    VariantProps<typeof accordionItemVariants> {}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(({ className, children, variant, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(accordionTriggerVariants({ variant, className }))}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const accordionContentVariants = cva("", {
  variants: {
    variant: {
      default:
        "overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      muted:
        "overflow-hidden pb-0 text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
    },
    spacing: {
      default: "pb-4 pt-0",
      muted: "pb-0 pt-2",
    },
  },
  defaultVariants: {
    variant: "default",
    spacing: "muted",
  },
});

export interface AccordionContentProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>,
    VariantProps<typeof accordionContentVariants> {}

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  AccordionContentProps
>(({ className, children, variant, spacing, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(accordionContentVariants({ variant, spacing, className }))}
    {...props}
  >
    <div
      className={cn(
        `${spacing === "muted" ? "pb-0 pt-2" : "pb-4 pt-0"}`,
        className,
      )}
    >
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
